import { FC } from "react"
import { ApplicationFileDownload } from "../widgets"
import clsx from "clsx"
import { ApplicationAttachment } from "../../modules/core/applications"
import { useAuth } from "../../modules/auth"
import { DLMDOC, DLMROI } from "../../modules/core/doclabel"
import { fieldChanged, getErrorMessage } from "../helpers"
import { KTSVG } from "../../../_metronic/helpers"
import { useFileUploadDialogState } from "../dialogs"
import { pickBy } from "lodash"
import axios from "axios";
import toast from 'react-hot-toast';
import { useQueryClient } from "react-query"

const API_URL = process.env.REACT_APP_API_URL;

export interface DetailFileModel {
  showFile: ApplicationAttachment
  showFileTitle: string
  hasChanged?: boolean
  appId?: number
  dlmAppId?: number
  docId?: number
  roiId?: number
  replaceField?: 'draftFileId' | 'signedFileId' | 'actualFileId' | 'cocFileId' | 'awardLetterId'
  // draftFileId?: number
  // signedFileId?: number
  // actualFileId?: number
  // cocFileId?: number
}


export const extractDOCDetailFiles = (docs : Array<DLMDOC>, dlmAppId: number) => {

  if (! (docs && docs.length > 0)) {
    return [];
  }
  
  let showFiles: Array<DetailFileModel> = [];
  
  docs.forEach(doc => {
    if (doc?.actualDOCFile) {
      showFiles.push({ showFile: doc.actualDOCFile, showFileTitle: 'Actual DOC', dlmAppId, docId: doc.id, replaceField: 'actualFileId', hasChanged: fieldChanged(doc, 'actualDOCFile')});            
    } else if (doc?.signedDOCFile) {
      showFiles.push({ showFile: doc.signedDOCFile, showFileTitle: 'Signed DOC', dlmAppId, docId: doc.id, replaceField: 'signedFileId', hasChanged: fieldChanged(doc, 'signedDOCFile')});            
    } else if (doc?.draftDOCFile) {
      showFiles.push({ showFile: doc.draftDOCFile, showFileTitle: 'Draft DOC', dlmAppId, docId: doc.id, replaceField: 'draftFileId', hasChanged: fieldChanged(doc, 'draftDOCFile')});            
    }    
  })

  return showFiles;
}


export const extractROIDetailFiles = (rois : Array<DLMROI>, dlmAppId: number) => {

  if (! (rois && rois.length > 0)) {
    return [];
  }
 
  let showFiles: Array<DetailFileModel> = [];
  
  rois.forEach(roi => {
    if (roi?.actualROIFile) {
      showFiles.push({ showFile: roi.actualROIFile, showFileTitle: 'Actual ROI', dlmAppId, roiId: roi.id, replaceField: 'actualFileId', hasChanged: fieldChanged(roi, 'actualROIFile')});            
    } else if (roi?.signedROIFile) {
      showFiles.push({ showFile: roi.signedROIFile, showFileTitle: 'Signed ROI', dlmAppId, roiId: roi.id, replaceField: 'signedFileId', hasChanged: fieldChanged(roi, 'signedROIFile')});            
    } else if (roi?.draftROIFile) {
      showFiles.push({ showFile: roi.draftROIFile, showFileTitle: 'Draft ROI', dlmAppId, roiId: roi.id, replaceField: 'draftFileId', hasChanged: fieldChanged(roi, 'draftROIFile')});            
    }    
  })

  return showFiles;
}


type Props = {
  labelText: string    
  file: ApplicationAttachment
  hasChanged?: boolean 
  wideLabel?: boolean
  setscoOnly?: boolean
  data?: DetailFileModel
  canReplace?: boolean
}

export const DetailFileDownload:FC<Props> = ({labelText, file, hasChanged = false, wideLabel = false, setscoOnly = false, data, canReplace}) => {

  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  const { initModal, toggleShow } = useFileUploadDialogState(state => state);

  const procFileReplace = async (values: any) => {
    
    if (! (values && values?.fileId && data && data?.replaceField)) {
      return;
    }

    const { fileId } = values;
    const { replaceField, appId, dlmAppId } = data;

    const _data = {
      ...pickBy(data, (value, key) => (value && ['appId', 'dlmAppId', 'docId', 'roiId'].includes(key))),
      ...pickBy(values, (value, key) => (value && ['note','noteAttachments'].includes(key))),
      [replaceField] : fileId,
    }

    await toast.promise(
      axios.post(`${API_URL}/EditFileServlet`, _data)
      .then( res => {
        if (! (res?.data && res.data?.success)) {
          return Promise.reject((res?.data && res.data?.message) ? res.data.message : 'Something happened');          
        }
        if (appId) queryClient.invalidateQueries(['GETAPPLICATION', `${appId}`]);
        if (dlmAppId) queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: dlmAppId} ]);
      }),
      {
        loading: 'Processing',
        success: 'File replaced.',
        error: (err) => `Operation unsuccessful : ${getErrorMessage(err)}`
      }
    );
  }

  const showReplaceDialog = () => {
    if (! data) return;
    initModal({
      title: `Replace ${data.showFileTitle}`,
      message: "Upload file to replace current file.",
      onOK: procFileReplace,
      hasNote: 'yes',
      hasAttachment: 'yes',
      submitText: "Submit",
      cancelText: 'Cancel',
    })
    toggleShow(true);
  }
  
  return (
  <div className="d-flex flex-row gap-6">
    {labelText && 
    <label
      className={clsx('fw-bold text-muted fs-7',
        { 'col-6' : wideLabel},
        { 'col-4' : !wideLabel},
        {'position-relative changed' : hasChanged})        
      }       
    >
      {labelText}
    </label>        
    }    
    <div 
      className={clsx(
        { 'col-6' : wideLabel},
        { 'col-8' : !wideLabel},
      )}
    >
      { (setscoOnly &&  (! currentUser?.isSetsco)) ? (
        <span className='fw-bolder fs-7 text-dark'>
        {(file && file.fileName) ? file.fileName : ''}
        </span>
      ) : (
        <div className="d-flex flex-row justify-content-start align-items-center gap-2">
          <ApplicationFileDownload file={file} />
          { (currentUser?.isSetsco && canReplace && data) && 
          <button type="button" className="btn btn-icon btn-sm p-0 h-15px w-15px text-warning text-hover-danger" onClick={showReplaceDialog} title="Replace File">
            <KTSVG path='/media/icons/duotune/files/fil011.svg' className='svg-icon-0 m-0' />
          </button>
          }
        </div>
      )}
    </div>
  </div>
  )
}
